import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { CoaParameter } from 'src/app/models/coa-parameter';
import { ProductResponse } from 'src/app/models/product.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { CoasParameterService } from 'src/app/services/coas-parameter.service';
import { ProductService } from 'src/app/services/product.service';

interface CoasParameterTemplateResponse {
  id: number;
  productCoasTemplateId: number;
  coasParameterId: number;
  companyId: number;
  createBy: number;
  creationDate: string;
  parameterData: CoaParameter;
}

interface ProductCoasTemplateResponse {
  id: number;
  name: string;
  productId: number;
  product: ProductResponse;
  companyId: number;
  parameters: CoasParameterTemplateResponse[];

  showDelete?: boolean;
  deleting?: boolean;
}

@Component({
  selector: 'app-parameters-products-templates',
  templateUrl: './parameters-products-templates.component.html',
  styleUrls: ['./parameters-products-templates.component.scss']
})
export class ParametersProductsTemplatesComponent implements OnInit {

  creating = false;
  loading = false;
  loadingData = false;

  editingTemplateId: number = null;

  filterWord = '';
  templateName = '';

  templates: ProductCoasTemplateResponse[] = [];
  filterTemplates: ProductCoasTemplateResponse[] = [];

  products: ProductResponse[] = [];
  filterProducts: ProductResponse[] = [];

  coasParameter: CoaParameter[] = [];
  filterCoasParameter: CoaParameter[] = [];

  currentProduct: ProductResponse = null;
  selectedParameters: CoaParameter[] = [];

  timeout = null;
  showParams: { [key: number]: boolean } = {};

  inputs = {
    product: {
      value: null,
      verified: false,
      required: true,
      message: 'Campo requerido',
      class: 'fa-list',
      color: '',
      tooltip: '',
      data: {
        id: null,
        measurement: null
      }
    },
    parameter: {
      value: null,
      verified: false,
      required: false,
      message: 'Campo requerido',
      class: 'fa-list',
      color: '',
      tooltip: '',
      data: {
        id: null
      }
    }
  };

  constructor(
    private productService: ProductService,
    private coasParameterService: CoasParameterService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.getProducts('');
    this.getParameters('');
    this.getTemplates('');
  }

  getProducts(clue: string) {
    this.productService.getProducts(clue).subscribe({
      next: (response: ResponseObject) => {
        this.products = response.data || [];
        this.filterProducts = this.products;

        if (this.filterProducts.length === 0) {
          this.changeInput(this.inputs.product, null, false, null, 'fa-xmark', null);
        } else {
          this.changeInput(this.inputs.product, clue, false, null, 'fa-list', null);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastrService.error(error.error?.message || 'Error al cargar productos');
      }
    });
  }

  getParameters(clue: string) {
    this.coasParameterService.getCoasParameters().subscribe({
      next: (response: ResponseObject) => {
        this.coasParameter = response.data || [];

        const filter = clue?.trim().toLowerCase();

        this.filterCoasParameter = this.coasParameter.filter(item =>
          !filter ||
          item.parameter?.toLowerCase().includes(filter) ||
          item.method?.toLowerCase().includes(filter) ||
          item.units?.toLowerCase().includes(filter)
        );

        if (this.filterCoasParameter.length === 0) {
          this.changeInput(this.inputs.parameter, null, false, null, 'fa-xmark', null);
        } else {
          this.changeInput(this.inputs.parameter, clue, false, null, 'fa-list', null);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastrService.error(error.error?.message || 'Error al cargar parámetros');
      }
    });
  }

  getTemplates(filterWord: string) {
    this.loadingData = true;

    this.coasParameterService.getTemplates(filterWord).subscribe({
      next: (response: ResponseObject) => {
        this.templates = response.data || [];
        this.filterTemplates = this.templates;
        this.loadingData = false;
      },
      error: (error: HttpErrorResponse) => {
        this.toastrService.error(error.error?.message || 'Error al cargar plantillas');
        this.loadingData = false;
      }
    });
  }

  writeProduct(input) {
    const inputValue = input.target.value;

    clearTimeout(this.timeout);
    this.inputs.product.class = 'typing';

    this.timeout = setTimeout(() => {
      this.getProducts(inputValue);
    }, 500);
  }

  checkProduct(product: ProductResponse) {
    this.inputs.product.class = 'fa-circle-check';
    this.inputs.product.verified = true;
    this.inputs.product.data = product;
    this.inputs.product.value = `${product.code} - ${product.name}`;
    this.currentProduct = product;
  }

  writeParameter(input) {
    const inputValue = input.target.value;

    clearTimeout(this.timeout);
    this.inputs.parameter.class = 'typing';

    this.timeout = setTimeout(() => {
      this.getParameters(inputValue);
    }, 500);
  }

  checkParameter(parameter: CoaParameter) {
    const exists = this.selectedParameters.some(item => item.id === parameter.id);

    if (exists) {
      this.toastrService.warning('Este parámetro ya fue agregado');
      this.clearParameterInput();
      return;
    }

    this.selectedParameters.push(parameter);
    this.clearParameterInput();
  }

  removeParameter(parameter: CoaParameter) {
    this.selectedParameters = this.selectedParameters.filter(item => item.id !== parameter.id);
  }

  saveTemplate() {
    if (this.editingTemplateId) {
      this.updateTemplate();
    } else {
      this.createTemplate();
    }
  }

  createTemplate() {
    if (!this.verifyForm) {
      this.toastrService.warning('Completa los campos requeridos');
      return;
    }

    const data = this.buildRequest();

    this.loading = true;

    this.coasParameterService.createTemplate(data).subscribe({
      next: () => {
        this.toastrService.success('Plantilla creada correctamente', 'Éxito');
        this.cleanForm();
        this.getTemplates(this.filterWord);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.toastrService.error(error.error?.message || 'Error al crear la plantilla');
        this.loading = false;
      }
    });
  }

  updateTemplate() {
    if (!this.verifyForm) {
      this.toastrService.warning('Completa los campos requeridos');
      return;
    }

    const data = this.buildRequest();

    this.loading = true;

    this.coasParameterService.updateTemplate(this.editingTemplateId, data).subscribe({
      next: () => {
        this.toastrService.success('Plantilla actualizada correctamente', 'Éxito');
        this.cleanForm();
        this.getTemplates(this.filterWord);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.toastrService.error(error.error?.message || 'Error al actualizar la plantilla');
        this.loading = false;
      }
    });
  }

  initEditing(template: ProductCoasTemplateResponse) {
    this.creating = true;
    this.editingTemplateId = template.id;
    this.templateName = template.name;

    this.currentProduct = template.product;

    this.inputs.product.value = `${template.product?.code} - ${template.product?.name}`;
    this.inputs.product.verified = true;
    this.inputs.product.class = 'fa-circle-check';
    this.inputs.product.data = this.currentProduct;

    this.selectedParameters = (template.parameters || [])
      .filter(item => item.parameterData)
      .map(item => ({
        id: item.coasParameterId,
        parameter: item.parameterData.parameter,
        method: item.parameterData.method,
        units: item.parameterData.units
      } as CoaParameter));
  }

  deleteTemplate(template: ProductCoasTemplateResponse) {
    template.deleting = true;
    template.showDelete = false;

    this.coasParameterService.deleteTemplate(template.id).subscribe({
      next: () => {
        this.toastrService.success('Plantilla eliminada correctamente', 'Éxito');

        if (this.editingTemplateId === template.id) {
          this.cleanForm();
        }

        this.getTemplates(this.filterWord);
      },
      error: (error: HttpErrorResponse) => {
        template.deleting = false;
        template.showDelete = false;
        this.toastrService.error(error.error?.message || 'Error al eliminar la plantilla');
      }
    });
  }

  showParamsTemplate(template: ProductCoasTemplateResponse) {
    this.showParams[template.id] = !this.showParams[template.id];
  }

  searching() {
    this.getTemplates(this.filterWord);
  }

  buildRequest() {
    return {
      name: this.templateName.trim(),
      productId: this.currentProduct.id,
      parameterIds: this.selectedParameters.map(item => item.id)
    };
  }

  cancelForm() {
    this.creating = false;
    this.cleanForm();
  }

  cleanForm() {
    this.templateName = '';
    this.currentProduct = null;
    this.selectedParameters = [];
    this.editingTemplateId = null;

    this.inputs.product.value = null;
    this.inputs.product.verified = false;
    this.inputs.product.class = 'fa-list';
    this.inputs.product.data = { id: null, measurement: null };

    this.clearParameterInput();
  }

  clearParameterInput() {
    this.inputs.parameter.value = null;
    this.inputs.parameter.verified = false;
    this.inputs.parameter.class = 'fa-list';
    this.inputs.parameter.data = { id: null };
  }

  changeInput(input, value, verified, required, classIcon, data) {
    input.value = value ?? input.value;
    input.verified = verified ?? input.verified;
    input.required = required ?? input.required;
    input.class = classIcon ?? input.class;
    input.data = data ?? input.data;
  }

  get verifyForm() {
    return !!this.templateName?.trim()
      && !!this.currentProduct
      && this.selectedParameters.length > 0;
  }

  get formTitle() {
    return this.editingTemplateId ? 'Editar plantilla' : 'Nueva plantilla';
  }

  get saveButtonText() {
    return this.editingTemplateId ? 'ACTUALIZAR' : 'GUARDAR';
  }

  showDeleteQuestion(template: ProductCoasTemplateResponse) {
    if (template.deleting) {
      return;
    }

    template.showDelete = !template.showDelete;
  }
}